import React, { useEffect, useState, useRef } from "react"
import useOutsideClick from "../hooks/useOutsideClick"
import { ID } from "../../model/types"
import classNames from "classnames"

export type AutocompleteInputStyle = "inset" | "form"
export type AutocompleteResult<Item extends { id: ID }> =
  | {
      type: "none"
    }
  | { type: "item"; item: Item }

export type AutocompleteOptions<Item extends { id: ID }> = {
  select: (item: AutocompleteResult<Item>, filter: string) => boolean
  showAbove?: boolean
  placeholder: string
  emptyMessage?: string
  fetchItems: (filter: string) => Item[]
  renderItem: (item: AutocompleteResult<Item>) => React.ReactNode

  // What to do when the user presses escape when the autocomplete is already closed (usually closes the parent context of the autocomplete)
  onEscape: () => void

  inputStyle: AutocompleteInputStyle
  canSelectNone?: boolean
}

const Autocomplete = <Item extends { id: ID }>(
  options: AutocompleteOptions<Item>
) => {
  const select = options.select
  const showAbove = options.showAbove ?? false
  const placeholder = options.placeholder
  const emptyMessage = options.emptyMessage ?? "None found"
  const fetchItems = options.fetchItems
  const renderItem = options.renderItem
  const onEscape = options.onEscape
  const inputStyle = options.inputStyle
  const canSelectNone = options.canSelectNone ?? false

  const [isOpen, setIsOpen] = useState(false)
  const [focus, setFocus] = useState(0)
  const [filter, setFilter] = useState("")
  const [items, setItems] = useState<Item[]>([])
  const containerRef = useRef<HTMLUListElement | null>(null)
  const ref = useOutsideClick(() => {
    close()
    return false
  })

  useEffect(() => {
    setItems(fetchItems(filter))
  }, [filter])

  useEffect(() => {
    if (isOpen && showAbove) scrollTo(items.length)
  }, [isOpen])

  const open = () => {
    setItems(fetchItems(filter))
    setIsOpen(true)
    setFocus(showAbove ? items.length : 1)
  }

  const close = () => {
    setIsOpen(false)
  }

  const changed = () => {
    if (!isOpen) open()
    else setItems(fetchItems(filter))
  }

  const scrollTo = (idx: number) => {
    if (containerRef.current) {
      const activeItem = containerRef.current.querySelector(
        `#item-${idx}`
      ) as HTMLElement
      if (activeItem)
        activeItem.scrollIntoView({
          behavior: "instant",
          block: "nearest",
          inline: "start",
        })
    }
  }

  const focusNext = (e) => {
    const total = items.length + (canSelectNone ? 1 : 0)
    let nextIndex: number | null = null
    if (focus > total) {
      nextIndex = 1
    } else {
      nextIndex = focus + 1
      if (nextIndex > total) {
        nextIndex = total
      }
    }
    if (isOpen) {
      setFocus(nextIndex)
      scrollTo(nextIndex)
    }
  }

  const focusPrev = (e) => {
    const total = items.length + (canSelectNone ? 1 : 0)
    let nextIndex: number | null = null
    if (focus > total) {
      nextIndex = total
    } else {
      nextIndex = focus - 1
      if (nextIndex < 1) {
        nextIndex = total
      }
    }
    if (isOpen) {
      setFocus(nextIndex)
      scrollTo(nextIndex)
    }
  }

  const handleKey = (e) => {
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault()
        focusPrev(e)
        break
      case "ArrowDown":
        e.preventDefault()
        focusNext(e)
        break
      case "Enter":
        e.preventDefault()
        if (focus == 1 && canSelectNone) {
          if (select({ type: "none" }, filter)) setFilter("")
        } else if (
          select(
            {
              type: "item",
              item: items[focus - 1 - (canSelectNone ? 1 : 0)],
            },
            filter
          )
        )
          setFilter("")
        break
      case "Escape":
        close()
        break
    }
  }

  let inputClassNames = ""

  switch (inputStyle) {
    case "inset":
      inputClassNames =
        "w-full rounded-md border-0 bg-stone-900/[0.04] py-1.5 text-sm text-stone-800 ring-1 ring-inset ring-stone-900/10 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-sky-400/40 dark:bg-white/[0.06] dark:text-stone-100 dark:ring-white/10 dark:placeholder:text-stone-500 dark:focus:ring-sky-400/30"
      break
    case "form":
      inputClassNames =
        "block w-full rounded-md shadow-sm  border-0 py-1.5 ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 dark:bg-gray-800 dark:text-gray-50 dark:disabled:bg-gray-700 dark:disabled:ring-gray-500 text-gray-900  ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400  focus:ring-indigo-600"
      break
  }

  return (
    <div className="autocomplete grow" ref={ref} onKeyUp={(e) => handleKey(e)}>
      <div className="relative flex grow flex-col justify-start gap-4">
        <input
          type="text"
          className={inputClassNames}
          name="filter"
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => open()}
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value)
            changed()
          }}
          onKeyDown={(e) => {
            if (e.key == "Escape") {
              if (isOpen) close()
              else onEscape()
            }
          }}
        />

        {isOpen && (
          <ul
            id="autocomplete-suggestions"
            className=" z-10 absolute right-0 left-0 py-1  flex max-h-56 flex-col overflow-y-auto scrollable rounded-md  bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-10 focus:outline-none sm:text-sm"
            ref={containerRef}
            style={showAbove ? { bottom: "110%" } : { top: "110%" }}
          >
            {canSelectNone && (
              <li
                key="none-option"
                id="item-none"
                className={classNames("py-2 pl-3 pr-9 text-sm", {
                  "bg-gray-100 dark:bg-gray-800": focus === 1,
                })}
                onMouseEnter={() => setFocus(1)}
                onClick={(e) => {
                  e.preventDefault()
                  if (select({ type: "none" }, filter)) setFilter("")
                }}
              >
                {renderItem({ type: "none" })}
              </li>
            )}
            {items.map((item, itemIndex) => {
              const ix = canSelectNone ? itemIndex + 2 : itemIndex + 1
              return (
                <li
                  key={item.id}
                  id={`item-${ix}`}
                  className={classNames("py-2 pl-3 pr-9 text-sm", {
                    "bg-gray-100 dark:bg-gray-800": focus === ix,
                  })}
                  onMouseEnter={() => setFocus(ix)}
                  onClick={(e) => {
                    e.preventDefault()
                    if (select({ type: "item", item }, filter)) setFilter("")
                  }}
                >
                  {renderItem({ type: "item", item })}
                </li>
              )
            })}
            {items.length == 0 ? (
              <div className="p-2 text-sm text-gray-700">{emptyMessage}</div>
            ) : null}
          </ul>
        )}
      </div>
    </div>
  )
}

export default Autocomplete
